import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { motion, AnimatePresence } from 'framer-motion';

const faqData = [
  {
    q: "What is MDeRCM and how does it help healthcare providers?",
    a: "MDeRCM is an AI-powered healthcare revenue cycle management services platform that automates insurance verification, claims processing, denials management, and more. As one of the best revenue cycle management companies, we help healthcare providers improve cash flow and reduce administrative burden through our comprehensive medical billing outsourcing services."
  },
  {
    q: "How does MDeRCM verify insurance coverage instantly?",
    a: "MDeRCM uses real-time APIs and AI agents that fetch, validate, and confirm a patient's coverage details before the appointment. Our outsourced revenue cycle management services ensure eligibility verification and reduce claim denials through advanced automation."
  },
  {
    q: "Does MDeRCM work with my existing EMR or EHR system?",
    a: "Yes. Our revenue cycle management consulting services integrate seamlessly with most EMR/EHR and billing platforms via APIs, HL7, or file-based automation. We provide medical revenue cycle management services that work with your existing technology stack."
  },
  {
    q: "Can MDeRCM handle Prior Authorization automatically?",
    a: "Yes. Our Prior Authorization AI Agent submits, monitors, and tracks authorizations—reducing delays in patient care and manual overhead. This is a key component of our hospital revenue cycle management solutions that streamline workflows for healthcare providers."
  },
  {
    q: "What happens if a claim is denied?",
    a: "MDeRCM's Denials Management Agent flags the issue, recommends appeal strategies, and ensures resubmission with improved accuracy to recover payments quickly. Our medical billing outsourcing companies infrastructure includes comprehensive denial management processes."
  },
  {
    q: "Is my data secure with MDeRCM?",
    a: "Absolutely. MDeRCM complies with HIPAA and industry best practices for data encryption, access control, and secure hosting infrastructure. As one of the leading revenue cycle management services companies, security is our top priority."
  },
  {
    q: "Can MDeRCM help reduce A/R aging and improve collections?",
    a: "Yes. Our Accounts Receivable AI Agent proactively follows up on outstanding claims, automates reminders, and escalates issues to accelerate collections. Learn how to improve revenue cycle management with our proven healthcare cost reduction strategies that reduce A/R days by 40-50%."
  },
  {
    q: "Does MDeRCM support multi-specialty or hospital billing?",
    a: "Yes. MDeRCM provides revenue cycle management services for hospitals, physician practices, diagnostic labs, dental offices, and mental health facilities with tailored RCM workflows. Our medical billing and coding outsourcing services support all specialties."
  },
  {
    q: "What setup or training is required to start using MDeRCM?",
    a: "Setup is minimal. Our team handles onboarding, integration, and provides full training and support through our revenue cycle management consulting services. Most clients using our medical billing outsourcing services go live within 1-2 weeks."
  },
  {
    q: "Can MDeRCM identify missing or incorrect CPT/ICD codes?",
    a: "Yes. Our Claims Processing Agent runs AI-based checks to detect errors in coding and suggest corrections before claims are submitted. This medical billing and coding outsourcing capability reduces denials and improves first-pass acceptance rates by 35-40%."
  },
  {
    q: "Is MDeRCM suitable for small practices as well as large hospitals?",
    a: "Absolutely. Whether you're a solo provider or a large hospital, MDeRCM scales to your needs. Our healthcare revenue cycle management services improve efficiency for organizations of all sizes across revenue cycle management services USA operations."
  },
  {
    q: "How are payments handled and posted in MDeRCM?",
    a: "Our Payment Posting Agent automatically applies payments, reconciles with EOBs, and flags discrepancies for faster financial closure. This is part of our comprehensive outsourced revenue cycle management services that streamline the entire payment process."
  },
  {
    q: "Can I get custom reports and insights from MDeRCM?",
    a: "Yes. MDeRCM offers detailed dashboards and reports for claims status, denials trends, A/R aging, and financial KPIs. Our healthcare cost reduction consulting includes advanced analytics to help you understand and optimize your revenue cycle performance."
  },
  {
    q: "How does MDeRCM help with compliance and credentialing?",
    a: "Our Credentialing Agent keeps provider profiles up to date, tracks expiration dates, and ensures claims are submitted only by approved providers. As one of the best revenue cycle management companies, we maintain 99.9% compliance accuracy."
  },
  {
    q: "What support options are available with MDeRCM?",
    a: "We provide dedicated onboarding support, live chat, phone, and email support—along with AI assistance built into the dashboard. Our revenue cycle management services companies infrastructure includes 24/7 support for all clients."
  }
];

const FAQSection = () => {
  const [activeIndex, setActiveIndex] = useState(null);

  const toggleFAQ = (index) => {
    setActiveIndex(activeIndex === index ? null : index);
  };

  return (
    <>
      <Helmet>
        {/* SEO Meta Tags */}
        <meta 
          name="description" 
          content="Frequently asked questions about MDeRCM's healthcare revenue cycle management services, medical billing outsourcing, and RCM automation. Learn how to improve revenue cycle management." 
        />
        <meta 
          name="keywords" 
          content="healthcare revenue cycle management services, medical billing outsourcing services, revenue cycle management FAQ, RCM questions, best revenue cycle management companies" 
        />
      </Helmet>

      <motion.div
        initial={{ opacity: 0, y: 100 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        viewport={{ once: true }}
        style={{ padding: '80px 60px', backgroundColor: '#ffffff' }}
      >
        {/* SEO-Optimized Header */}
        <motion.h2
          initial={{ scale: 0.8, opacity: 0 }}
          whileInView={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.5 }}
          style={{
            textAlign: 'center',
            fontSize: '36px',
            fontWeight: '700',
            color: '#001F2B',
            marginBottom: '20px'
          }}
        >
          Healthcare Revenue Cycle Management Services - Frequently Asked Questions
        </motion.h2>

        {/* SEO-Rich Subtitle */}
        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.5 }}
          style={{
            textAlign: 'center',
            fontSize: '17px',
            color: '#555',
            maxWidth: '850px',
            margin: '0 auto 40px',
            lineHeight: '1.7'
          }}
        >
          Get answers about our <strong>medical billing outsourcing services</strong>, 
          <strong> outsourced revenue cycle management services</strong>, and learn 
          <strong> how to improve revenue cycle management</strong> with proven solutions from one of the 
          <strong> best revenue cycle management companies</strong> in the USA.
        </motion.p>

        {/* FAQ Items */}
        <div style={{ maxWidth: '900px', margin: '0 auto' }}>
          {faqData.map((faq, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.05, duration: 0.5 }}
              style={{
                borderBottom: '1px solid #ddd',
                padding: '20px 0',
                borderRadius: '10px',
                overflow: 'hidden'
              }}
            >
              <div
                onClick={() => toggleFAQ(index)}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  cursor: 'pointer'
                }}
              >
                <h3 style={{ 
                  fontSize: '18px', 
                  fontWeight: '600', 
                  color: '#001F2B', 
                  margin: 0,
                  flex: 1,
                  paddingRight: '20px'
                }}>
                  {faq.q}
                </h3>
                <span style={{ 
                  fontSize: '24px', 
                  color: '#F26522',
                  minWidth: '30px',
                  textAlign: 'center',
                  fontWeight: 'bold'
                }}>
                  {activeIndex === index ? '−' : '+'}
                </span>
              </div>

              <AnimatePresence initial={false}>
                {activeIndex === index && (
                  <motion.div
                    key="answer"
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.4 }}
                    style={{
                      overflow: 'hidden'
                    }}
                  >
                    <p style={{
                      fontSize: '16px',
                      color: '#333',
                      lineHeight: '1.7',
                      marginTop: '15px',
                      marginBottom: 0
                    }}>
                      {faq.a}
                    </p>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </div>

        {/* Additional SEO Keywords Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.6 }}
          viewport={{ once: true }}
          style={{
            marginTop: '60px',
            padding: '40px',
            backgroundColor: '#f8f9fa',
            borderRadius: '15px',
            maxWidth: '900px',
            margin: '60px auto 0',
            textAlign: 'center'
          }}
        >
          <h3 style={{
            fontSize: '24px',
            fontWeight: '700',
            color: '#001F2B',
            marginBottom: '20px'
          }}>
            Why Choose MDeRCM for Revenue Cycle Management?
          </h3>
          <p style={{
            fontSize: '16px',
            lineHeight: '1.8',
            color: '#555',
            marginBottom: 0
          }}>
            As specialists among <strong>revenue cycle management services companies</strong>, we provide comprehensive 
            <strong> revenue cycle management services for hospitals</strong> and healthcare providers. 
            Our <strong>medical billing outsourcing companies</strong> infrastructure combines 
            <strong> medical billing and coding outsourcing</strong> expertise with AI automation. 
            Experience proven <strong>healthcare cost reduction consulting</strong> strategies that deliver 
            measurable results. Our <strong>revenue cycle management consulting services</strong> help you understand 
            <strong> how to improve revenue cycle management</strong> with industry-leading 
            <strong> healthcare cost reduction strategies</strong>. Join thousands of satisfied clients across 
            <strong> revenue cycle management services USA</strong> who trust MDeRCM for their 
            <strong> medical revenue cycle management services</strong> needs.
          </p>
        </motion.div>

        {/* CTA Section */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          whileInView={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.4, duration: 0.5 }}
          viewport={{ once: true }}
          style={{
            textAlign: 'center',
            marginTop: '50px'
          }}
        >
          <p style={{
            fontSize: '18px',
            color: '#333',
            marginBottom: '25px',
            fontWeight: '500'
          }}>
            Still have questions about our <strong>healthcare revenue cycle management services</strong>?
          </p>
          <motion.a
            href="/free-trial"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            style={{
              display: 'inline-block',
              padding: '16px 40px',
              backgroundColor: '#F26522',
              color: '#fff',
              borderRadius: '30px',
              fontSize: '16px',
              fontWeight: '700',
              textDecoration: 'none',
              boxShadow: '0 6px 20px rgba(242, 101, 34, 0.4)',
              transition: 'all 0.3s ease',
              cursor: 'pointer'
            }}
          >
            Contact Our RCM Experts
          </motion.a>
        </motion.div>

        {/* ✅ REMOVED: Duplicate FAQ Schema - Already exists in Home.js */}
        {/* FAQ Schema is now ONLY in Home.js to avoid duplication */}
      </motion.div>
    </>
  );
};

export default FAQSection;